// Exception Report Data Transformer
// Transforms flat API response to hierarchical structure expected by the component

export interface ApiRideData {
  rideId: string;
  objectName: string;
  licensePlate: string | null;
  start: string;
  end: string;
  startMileageKm: number;
  endMileageKm: number;
  incompleteness: string;
  technicalCause: string;
  warning: string;
}

export interface TransformedIncompleteField {
  field: string;
  technicalCause: string;
  warning: string;
}

export interface TransformedRide {
  rideId: string;
  startDate: string;
  endDate: string;
  startMileage: number;
  endMileage: number;
  incompleteFields: TransformedIncompleteField[];
}

export interface TransformedVehicle {
  id: string;
  vehicleInfo: {
    type: string;
    licensePlate: string;
  };
  rides: TransformedRide[];
  hasIncompleteFields: boolean;
}

/**
 * Transforms flat API response to hierarchical structure
 * Groups rides by vehicle (objectName) and problems by rideId
 */
export const transformApiDataToHierarchical = (
  flatRides: ApiRideData[]
): TransformedVehicle[] => {
  const groupedByVehicle = flatRides.reduce((acc: any, ride: ApiRideData) => {
    const vehicleKey = ride.objectName || "Unknown Vehicle";

    if (!acc[vehicleKey]) {
      acc[vehicleKey] = {
        id: vehicleKey,
        vehicleInfo: {
          type: ride.objectName || "Unknown Vehicle",
          licensePlate: ride.licensePlate || "N/A"
        },
        ridesMap: new Map()
      };
    }

    const rideKey = ride.rideId;

    if (!acc[vehicleKey].ridesMap.has(rideKey)) {
      acc[vehicleKey].ridesMap.set(rideKey, {
        rideId: ride.rideId,
        startDate: ride.start,
        endDate: ride.end,
        startMileage: ride.startMileageKm,
        endMileage: ride.endMileageKm,
        incompleteFields: []
      });
    }

    acc[vehicleKey].ridesMap.get(rideKey).incompleteFields.push({
      field: ride.incompleteness,
      technicalCause: ride.technicalCause,
      warning: ride.warning
    });

    return acc;
  }, {});

  const hierarchicalData = Object.values(groupedByVehicle).map(
    (vehicle: any) => ({
      id: vehicle.id,
      vehicleInfo: vehicle.vehicleInfo,
      rides: Array.from(vehicle.ridesMap.values()),
      hasIncompleteFields: vehicle.ridesMap.size > 0
    })
  );

  return hierarchicalData as TransformedVehicle[];
};

/**
 * Extracts data array from API response
 * Handles both array and object with data property structures
 */
export const extractApiData = (report: any): ApiRideData[] => {
  if (Array.isArray(report)) {
    return report;
  } else if (
    report &&
    typeof report === "object" &&
    "data" in report &&
    Array.isArray(report.data)
  ) {
    return report.data;
  }

  return [];
};
